// ────────────────────────────────────────────────────────────────────────────
// Label overrides.
//
// Several bands print a fixed set of rows whose *values* come from the document
// but whose *labels* an organisation may word differently — "PO number" as
// "Your order ref", "Subtotal (ex VAT)" as "Goods total", "VAT" as "Sales Tax".
// The pattern is the same everywhere: a sparse map keyed by the row, where a
// missing key means "use the standard label".
// ────────────────────────────────────────────────────────────────────────────

/** Sparse per-row label overrides. Missing key = use the standard label. */
export type LabelOverrides<K extends string> = Partial<Record<K, string>>;

/**
 * Set or clear one label. A blank value **deletes** the key rather than storing
 * an empty string, so a cleared box round-trips as "standard label" instead of
 * leaving a blank that would print as nothing.
 */
export function withLabelOverride<K extends string>(
  labels: LabelOverrides<K> | undefined,
  key: K,
  value: string,
): LabelOverrides<K> {
  const next: LabelOverrides<K> = { ...labels };
  if (value.trim()) next[key] = value;
  else delete next[key];
  return next;
}

/** The label to print: the override if set, otherwise the standard one. */
export function resolveLabel<K extends string>(
  labels: LabelOverrides<K> | undefined,
  key: K,
  standard: Record<K, string>,
): string {
  return labels?.[key]?.trim() || standard[key];
}
